"use client";

import { useState, useMemo } from "react";
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, X, TrendingUp, ExternalLink, ChevronDown } from "lucide-react";
import MiniDualChart from "@/app/components/MiniDualChart";
import { BsPin, BsPinFill } from "react-icons/bs";
import Link from "next/link";
import { useRouter } from "next/navigation";
import dynamic from "next/dynamic";
import { usePinnedBoxes, PinnedBox } from "@/contexts/PinnedBoxContext";
import { useRealtimeMarketPrice } from "@/hooks/useRealtimeMarketPrice";
import { useT } from "@/hooks/useT";

const TradingModal = dynamic(
  () => import("@/app/components/TradingModal"),
  { ssr: false }
);

interface Market {
  id: string;
  slug: string;
  eventSlug?: string | null;
  title: string;
  image: string | null;
  yesPrice: number;
  noPrice: number;
  volume24hr: number;
  view: "orderbook" | "trades" | "chart";
  type: "market" | "event";
  markets?: any[];
}

interface EventTileProps {
  market: Market;
  onRemove: (id: string) => void;
}

// Per-outcome row inside a multi-outcome event tile. Holds its own
// `expanded` state so opening one outcome's chart doesn't re-render
// the rest of the list, and the chart fetch is lazy — `MarketSparkline`
// only mounts (and hits /api/polymarket/prices-history) when the user
// expands the row. Keeps the default render cheap even on a 32-outcome
// event like FIFA.
function OutcomeRow({
  m,
  yesPrice,
  noPrice,
  parentImage,
  eventSlug,
  onOpenTradingModal,
}: {
  m: any;
  yesPrice: number;
  noPrice: number;
  parentImage: string | null;
  eventSlug: string | null;
  onOpenTradingModal: (outcome: "YES" | "NO") => void;
}) {
  const t = useT();
  const router = useRouter();
  const [expanded, setExpanded] = useState(false);

  const eventHref = eventSlug
    ? `/polymarket/event/${eventSlug}?market=${m.slug}`
    : null;
  const outcomeLabel = m.groupItemTitle || m.question;
  const outcomeImg = m.image || parentImage;
  const change24h: number | null =
    typeof m.oneDayPriceChange === "number" ? m.oneDayPriceChange : null;
  const isUp = (change24h ?? 0) >= 0;
  const showChange = change24h != null && Math.abs(change24h) >= 0.001;

  // MiniDualChart parses clobTokenIds itself (string or array). We
  // only need to know if it has data to decide whether the chevron
  // toggle is meaningful — pulling out the array once here avoids
  // mounting the chart just to discover it'd be unavailable.
  const hasChartData: boolean = (() => {
    const raw = m.clobTokenIds;
    if (!raw) return false;
    try {
      const ids = typeof raw === "string" ? JSON.parse(raw) : raw;
      return Array.isArray(ids) && ids.length >= 2;
    } catch {
      return false;
    }
  })();

  const labelInner = (
    <span className="flex items-center gap-2 min-w-0 flex-1">
      {outcomeImg ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={outcomeImg}
          alt=""
          className="w-5 h-5 rounded-[5px] object-cover flex-shrink-0 bg-gray-100 dark:bg-white/[0.05]"
        />
      ) : (
        <span className="w-5 h-5 rounded-[5px] bg-gray-200 dark:bg-white/[0.05] flex-shrink-0" />
      )}
      <span className="truncate flex-1">{outcomeLabel}</span>
      {showChange && (
        <span
          className={`text-[10px] font-bold tabular-nums px-1 py-px rounded flex-shrink-0 ${
            isUp
              ? "text-emerald-600 dark:text-emerald-400 bg-emerald-500/10"
              : "text-red-600 dark:text-red-400 bg-red-500/10"
          }`}
        >
          {isUp ? "+" : ""}{(change24h! * 100).toFixed(1)}%
        </span>
      )}
    </span>
  );

  return (
    <div className="p-3 bg-gray-50 dark:bg-white/[0.02] border border-gray-200 dark:border-[#1A1A1A] rounded-[5px] hover:border-gray-300 dark:hover:border-white/[0.1] transition-colors">
      {/* Row top — label + chevron toggle. The chevron only renders
          when we have a YES tokenId (no point opening to an empty
          chart). */}
      <div className="flex items-center gap-2 mb-2">
        {eventHref ? (
          <Link
            href={eventHref}
            prefetch={false}
            className="flex-1 min-w-0 text-sm font-semibold text-gray-900 dark:text-white hover:text-[#CCFF00] transition-colors"
            title={m.question}
          >
            {labelInner}
          </Link>
        ) : (
          <h4 className="flex-1 min-w-0 text-sm font-semibold text-gray-900 dark:text-white" title={m.question}>
            {labelInner}
          </h4>
        )}
        {hasChartData && (
          <button
            onClick={() => setExpanded((e) => !e)}
            aria-label={expanded ? "Hide chart" : "Show chart"}
            className="p-1 -mr-1 rounded-[5px] text-gray-400 hover:text-gray-700 dark:hover:text-white hover:bg-gray-200/50 dark:hover:bg-white/[0.05] transition-colors flex-shrink-0"
          >
            <ChevronDown
              className={`w-3.5 h-3.5 transition-transform ${expanded ? "rotate-180" : ""}`}
            />
          </button>
        )}
      </div>

      {/* YES / NO buttons — same soft-tinted pill style as binary
          MarketTile so the visual language stays consistent. */}
      <div className="grid grid-cols-2 gap-2">
        <button
          onClick={() => {
            if (eventHref) router.push(`${eventHref}&outcome=yes`);
            else onOpenTradingModal("YES");
          }}
          className="py-2 rounded-[5px] font-inter-tight font-bold text-xs tabular-nums transition-colors bg-[#359a5e]/15 text-[#3db468] hover:bg-[#359a5e]/25"
        >
          {t("bub.label.yes")} {(yesPrice * 100) < 0.05 ? '0.0' : (yesPrice * 100).toFixed(1)}¢
        </button>
        <button
          onClick={() => {
            if (eventHref) router.push(`${eventHref}&outcome=no`);
            else onOpenTradingModal("NO");
          }}
          className="py-2 rounded-[5px] font-inter-tight font-bold text-xs tabular-nums transition-colors bg-[#cb3131]/15 text-[#cb3131] hover:bg-[#cb3131]/25"
        >
          {t("bub.label.no")} {(noPrice * 100) < 0.05 ? '0.0' : (noPrice * 100).toFixed(1)}¢
        </button>
      </div>

      {/* Chart — lazy mount on expand. Same dual YES/NO area chart as
          the binary MarketTile (1w lookback). MiniDualChart handles
          the parsing of clobTokenIds + the parallel history fetch. */}
      {expanded && hasChartData && (
        <div className="mt-2 pt-2 border-t border-gray-200 dark:border-[#1A1A1A] h-[120px]">
          <MiniDualChart clobTokenIds={m.clobTokenIds} id={String(m.id)} interval="1w" />
        </div>
      )}
    </div>
  );
}

export default function EventTile({ market, onRemove }: EventTileProps) {
  const t = useT();
  const router = useRouter();
  const [tradingModal, setTradingModal] = useState<{
    isOpen: boolean;
    outcome: "YES" | "NO";
    marketSlug: string;
    marketQuestion: string;
    marketImage: string | null;
    yesPrice: number;
    noPrice: number;
    outcomes?: string[];
  } | null>(null);
  const { addPinnedBox, removePinnedBox, isPinned } = usePinnedBoxes();
  const pinned = isPinned(market.id);

  // Transform markets for real-time prices hook
  const marketsForHook = useMemo(() => {
    return (market.markets || []).slice(0, 10).map((m: any) => ({
      ...m,
      conditionId: m.conditionId || m.id,
    }));
  }, [market.markets]);

  // Get real-time prices from WebSocket
  const { getBuyPrices } = useRealtimeMarketPrice({
    markets: marketsForHook,
  });

  const handleTogglePin = () => {
    if (pinned) {
      removePinnedBox(market.id);
    } else {
      const pinnedBox: PinnedBox = {
        id: market.id,
        slug: market.slug,
        eventSlug: market.eventSlug ?? null,
        title: market.title,
        image: market.image,
        yesPrice: market.yesPrice,
        noPrice: market.noPrice,
        volume24hr: market.volume24hr,
        view: market.view,
        type: "event",
        markets: market.markets,
      };
      addPinnedBox(pinnedBox);
    }
  };

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: market.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] overflow-hidden flex flex-col h-[400px] transition-colors hover:border-gray-300 dark:hover:border-white/[0.1]"
    >
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-[#1A1A1A] flex items-center justify-between gap-2">
        <button
          {...attributes}
          {...listeners}
          className="p-1.5 hover:bg-gray-100 dark:hover:bg-white/[0.06] rounded-[5px] cursor-grab active:cursor-grabbing transition-colors"
          aria-label={t("degen.tile.dragReorder")}
          title={t("degen.tile.dragReorder")}
        >
          <GripVertical className="w-4 h-4 text-gray-400" />
        </button>

        <div className="flex-1 flex items-center gap-2 min-w-0">
          {market.image && (
            <img
              src={market.image}
              alt={market.title}
              className="w-6 h-6 rounded-[5px] object-cover flex-shrink-0"
            />
          )}
          <h3 className="font-semibold text-sm text-gray-900 dark:text-white truncate" title={market.title}>
            {market.title}
          </h3>
        </div>

        <div className="flex items-center gap-0.5">
          <button
            onClick={handleTogglePin}
            className={`p-1.5 rounded-[5px] transition-colors ${
              pinned
                ? "bg-gray-900/10 dark:bg-white/10 text-gray-900 dark:text-white"
                : "hover:bg-gray-100 dark:hover:bg-white/[0.06] text-gray-400"
            }`}
            aria-label={pinned ? t("degen.tile.unpinEvent") : t("degen.tile.pinEvent")}
            title={pinned ? t("degen.tile.unpinEvent") : t("degen.tile.pinEvent")}
          >
            {pinned ? (
              <BsPinFill className="w-3.5 h-3.5" />
            ) : (
              <BsPin className="w-3.5 h-3.5" />
            )}
          </button>

          {/* External-link to the event page (was missing on the
              event tile — only the market tile had it). Click goes to
              /polymarket/event/<slug> which renders the full event
              with all sub-markets. Same affordance MarketTile gives. */}
          {market.eventSlug && (
            <Link
              href={`/polymarket/event/${market.eventSlug}`}
              prefetch={false}
              className="p-1.5 hover:bg-gray-100 dark:hover:bg-white/[0.06] rounded-[5px] transition-colors"
              aria-label={t("degen.tile.openEvent")}
              title={t("degen.tile.openEvent")}
            >
              <ExternalLink className="w-3.5 h-3.5 text-gray-400" />
            </Link>
          )}

          <button
            onClick={() => onRemove(market.id)}
            className="p-1.5 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-[5px] transition-colors"
            aria-label={t("degen.tile.removeEvent")}
            title={t("degen.tile.removeEvent")}
          >
            <X className="w-3.5 h-3.5 text-gray-400 hover:text-red-500" />
          </button>
        </div>
      </div>

      {/* Markets List — every clickable surface (question text + YES
          + NO) now routes to the event page with the right sub-market
          and outcome preselected, instead of stacking a TradingModal
          over the DegenBox grid. Same pattern as MarketTile and
          PolymarketMarketCard — one canonical "open this trade" flow
          across the app. */}
      <div className="flex-1 overflow-y-auto p-3">
        <div className="space-y-2">
          {market.markets?.slice(0, 10).map((m: any) => {
            const { buyYes, buyNo } = getBuyPrices(m);
            return (
              <OutcomeRow
                key={m.id}
                m={m}
                yesPrice={buyYes}
                noPrice={buyNo}
                parentImage={market.image}
                eventSlug={market.eventSlug || null}
                onOpenTradingModal={(outcome) => setTradingModal({
                  isOpen: true,
                  outcome,
                  marketSlug: m.slug,
                  marketQuestion: m.question,
                  marketImage: market.image,
                  yesPrice: buyYes,
                  noPrice: buyNo,
                  outcomes: m.outcomes,
                })}
              />
            );
          })}
          {market.markets && market.markets.length > 10 && (
            <div className="text-center py-2">
              <span className="text-[11px] text-gray-400">
                {t("degen.tile.moreMarkets", { n: market.markets.length - 10 })}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Footer Stats */}
      <div className="p-3 border-t border-gray-200 dark:border-[#1A1A1A] bg-gray-50 dark:bg-white/[0.02]">
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-1 text-gray-500">
            <TrendingUp className="w-3 h-3 text-gray-400" />
            <span>{t("degen.tile.vol24h")}</span>
          </div>
          <span className="font-semibold text-gray-900 dark:text-white tabular-nums">
            ${market.volume24hr >= 1000000
              ? `${(market.volume24hr / 1000000).toFixed(1)}M`
              : `${(market.volume24hr / 1000).toFixed(1)}K`}
          </span>
        </div>
      </div>

      {/* Trading Modal */}
      {tradingModal?.isOpen && (
        <TradingModal
          isOpen={tradingModal.isOpen}
          onClose={() => setTradingModal(null)}
          marketSlug={tradingModal.marketSlug}
          outcome={tradingModal.outcome}
          outcomes={tradingModal.outcomes}
          outcomePrice={
            tradingModal.outcome === "YES"
              ? tradingModal.yesPrice
              : tradingModal.noPrice
          }
          marketQuestion={tradingModal.marketQuestion}
          marketImage={tradingModal.marketImage}
        />
      )}
    </div>
  );
}
